import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { RefreshCw, Eye, Filter } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [updateData, setUpdateData] = useState({ status: '', notes: '' });

  const statusColors = {
    'New': 'bg-blue-100 text-blue-800 border-blue-200',
    'Contacted': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'In Review': 'bg-purple-100 text-purple-800 border-purple-200',
    'Accepted': 'bg-green-100 text-green-800 border-green-200',
    'Not a Fit': 'bg-gray-100 text-gray-800 border-gray-200'
  };

  const fetchReferrals = useCallback(async (status = null) => {
    setLoading(true);
    try {
      const url = status && status !== 'all' 
        ? `${BACKEND_URL}/api/referrals?status=${encodeURIComponent(status)}`
        : `${BACKEND_URL}/api/referrals`;
      
      const response = await axios.get(url);
      setReferrals(response.data);
    } catch (error) {
      console.error('Error fetching referrals:', error);
      toast.error('Failed to load referrals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReferrals(statusFilter !== 'all' ? statusFilter : null);
  }, [statusFilter, fetchReferrals]);

  const handleUpdate = async (referralId) => {
    try {
      const payload = {};
      if (updateData.status) payload.status = updateData.status;
      if (updateData.notes) payload.notes = updateData.notes;

      await axios.patch(`${BACKEND_URL}/api/referrals/${referralId}`, payload);
      
      toast.success('Referral updated successfully');
      fetchReferrals(statusFilter !== 'all' ? statusFilter : null);
      setSelectedReferral(null);
      setUpdateData({ status: '', notes: '' });
    } catch (error) {
      console.error('Error updating referral:', error);
      toast.error('Failed to update referral');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-cream-50 py-12 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-sage-900 mb-2">Referral Tracking System</h1>
          <p className="text-sage-700">Manage and track all referral inquiries</p>
        </div>

        {/* Filters and Actions */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex items-center gap-2 flex-1">
            <Filter className="w-5 h-5 text-sage-600" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-64 border-sage-200">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Referrals</SelectItem>
                <SelectItem value="New">New</SelectItem>
                <SelectItem value="Contacted">Contacted</SelectItem>
                <SelectItem value="In Review">In Review</SelectItem>
                <SelectItem value="Accepted">Accepted</SelectItem>
                <SelectItem value="Not a Fit">Not a Fit</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button 
            onClick={() => fetchReferrals(statusFilter !== 'all' ? statusFilter : null)}
            variant="outline"
            className="border-sage-300 text-sage-700 hover:bg-sage-50"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Referrals List */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-sage-600 mx-auto" />
            <p className="mt-4 text-sage-700">Loading referrals...</p>
          </div>
        ) : referrals.length === 0 ? (
          <Card className="p-12 text-center bg-white">
            <p className="text-sage-700 text-lg">No referrals found</p>
          </Card>
        ) : (
          <div className="grid gap-4">
            {referrals.map((referral) => (
              <Card key={referral.id} className="p-6 bg-white border-2 border-sage-100 hover:border-sage-300 transition-all">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start gap-3 mb-3">
                      <div>
                        <h3 className="text-xl font-bold text-sage-900">{referral.name}</h3>
                        {referral.organization && (
                          <p className="text-sage-600">{referral.organization}</p>
                        )}
                      </div>
                      <Badge className={`${statusColors[referral.status]} border`}>
                        {referral.status}
                      </Badge>
                    </div>

                    <div className="grid md:grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="font-semibold text-sage-800">Email:</span>
                        <span className="text-sage-700 ml-2">{referral.email}</span>
                      </div>
                      <div>
                        <span className="font-semibold text-sage-800">Phone:</span>
                        <span className="text-sage-700 ml-2">{referral.phone}</span>
                      </div>
                      <div>
                        <span className="font-semibold text-sage-800">Resident Type:</span>
                        <span className="text-sage-700 ml-2">{referral.residentType}</span>
                      </div>
                      <div>
                        <span className="font-semibold text-sage-800">Funding:</span>
                        <span className="text-sage-700 ml-2">{referral.fundingSource}</span>
                      </div>
                    </div>

                    <div className="mt-3 text-sm text-sage-600">
                      Submitted: {formatDate(referral.dateSubmitted)}
                    </div>
                  </div>

                  <Dialog>
                    <DialogTrigger asChild>
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setSelectedReferral(referral);
                          setUpdateData({ 
                            status: referral.status, 
                            notes: referral.notes || '' 
                          });
                        }}
                        className="border-sage-300 text-sage-700 hover:bg-sage-50"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Details
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="text-2xl text-sage-900">Referral Details</DialogTitle>
                      </DialogHeader>
                      
                      {selectedReferral && (
                        <div className="space-y-6">
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <Label className="text-sage-800 font-semibold">Name</Label>
                              <p className="text-sage-700 mt-1">{selectedReferral.name}</p>
                            </div>
                            {selectedReferral.organization && (
                              <div>
                                <Label className="text-sage-800 font-semibold">Organization</Label>
                                <p className="text-sage-700 mt-1">{selectedReferral.organization}</p>
                              </div>
                            )}
                            <div>
                              <Label className="text-sage-800 font-semibold">Email</Label>
                              <p className="text-sage-700 mt-1">{selectedReferral.email}</p>
                            </div>
                            <div>
                              <Label className="text-sage-800 font-semibold">Phone</Label>
                              <p className="text-sage-700 mt-1">{selectedReferral.phone}</p>
                            </div>
                            <div>
                              <Label className="text-sage-800 font-semibold">Resident Type</Label>
                              <p className="text-sage-700 mt-1">{selectedReferral.residentType}</p>
                            </div>
                            <div>
                              <Label className="text-sage-800 font-semibold">Funding Source</Label>
                              <p className="text-sage-700 mt-1">{selectedReferral.fundingSource}</p>
                            </div>
                          </div>

                          {selectedReferral.message && (
                            <div>
                              <Label className="text-sage-800 font-semibold">Message</Label>
                              <p className="text-sage-700 mt-2 p-4 bg-sage-50 rounded-lg">
                                {selectedReferral.message}
                              </p>
                            </div>
                          )}

                          <div>
                            <Label className="text-sage-800 font-semibold">Date Submitted</Label>
                            <p className="text-sage-700 mt-1">{formatDate(selectedReferral.dateSubmitted)}</p>
                          </div>

                          <div className="pt-6 border-t border-sage-200">
                            <Label htmlFor="status" className="text-sage-800 font-semibold">
                              Update Status
                            </Label>
                            <Select 
                              value={updateData.status} 
                              onValueChange={(value) => setUpdateData({...updateData, status: value})}
                            >
                              <SelectTrigger className="mt-2 border-sage-200">
                                <SelectValue placeholder="Select status" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="New">New</SelectItem>
                                <SelectItem value="Contacted">Contacted</SelectItem>
                                <SelectItem value="In Review">In Review</SelectItem>
                                <SelectItem value="Accepted">Accepted</SelectItem>
                                <SelectItem value="Not a Fit">Not a Fit</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <Label htmlFor="notes" className="text-sage-800 font-semibold">
                              Internal Notes
                            </Label>
                            <Textarea
                              id="notes"
                              value={updateData.notes}
                              onChange={(e) => setUpdateData({...updateData, notes: e.target.value})}
                              rows={4}
                              className="mt-2 border-sage-200"
                              placeholder="Add internal notes about this referral..."
                            />
                          </div>

                          <Button 
                            onClick={() => handleUpdate(selectedReferral.id)}
                            className="w-full bg-sage-700 hover:bg-sage-800 text-white"
                          >
                            Save Changes
                          </Button>
                        </div>
                      )}
                    </DialogContent>
                  </Dialog>
                </div>

                {referral.notes && (
                  <div className="mt-4 pt-4 border-t border-sage-100">
                    <Label className="text-sage-800 font-semibold text-sm">Notes:</Label>
                    <p className="text-sage-700 text-sm mt-1">{referral.notes}</p>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
